import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FormLayoutRenderer from '../../components/generic/FormLayoutRenderer';
import { renderField } from '../../components/generic/fieldRenderers';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Badge from '../../components/ui/Badge';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { Button, Select, useConfirm } from '../../components/ui';
import {
  CalendarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  DownloadIcon,
  PencilIcon,
  TrashIcon,
  UndoIcon,
  UserIcon,
} from '../../components/ui/Icons';
import { formatDateTime } from '../../config/appConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { cn } from '../../lib/utils';
import {
  deleteEFormSubmission,
  exportEFormSubmissionLayout,
  exportEFormSubmissionTemplate,
  revertEFormSubmissionToDraft,
  submitEFormSubmission,
} from '../../services/mutations';
import { ApprovalNoticeBanner, ApprovalNoticeIcon } from './ApprovalNoticeIcon';
import {
  compileFormSchema,
  applyEFormRoleAccess,
  coerceEFormFiles,
  flattenSchemaFields,
  formatFieldDisplayValue,
  isEFormSchema,
  isEFormDisplayField,
  type EFormFieldSchema,
  type EFormSchema,
  type EFormSectionSchema,
} from './schema';
import { maybeEFormDisplayNode } from './DisplayField';
import { EFormFileField } from './FileField';
import { EFormPhotoField } from './PhotoField';
import { EFormSignatureField } from './SignatureField';
import { EFormSubformField } from './SubformField';
import { submitterDisplayName, useEFormAccessContext, useEFormSubmission } from './useData';
import { listPrintTemplates } from './printLayoutModel';

function MetaRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  const { colors } = useTheme();
  return (
    <div className="flex items-start gap-3">
      <span className={cn('mt-0.5 shrink-0', colors.textMuted)}>{icon}</span>
      <div className="min-w-0">
        <div className={cn('text-[11px] uppercase tracking-wide', colors.textMuted)}>{label}</div>
        <div className={cn('text-sm font-medium break-words', colors.text)}>{value}</div>
      </div>
    </div>
  );
}

function FieldValueBlock({
  field,
  raw,
}: {
  field: EFormFieldSchema;
  raw: unknown;
}) {
  const { colors } = useTheme();
  const display = formatFieldDisplayValue(field, raw);
  const full =
    field.type === 'textarea' && raw != null && String(raw).length > 80 ? String(raw) : undefined;
  const fileList =
    field.type === 'file' || coerceEFormFiles(raw).length > 0 ? coerceEFormFiles(raw) : null;

  if (isEFormDisplayField(field)) {
    return <div>{maybeEFormDisplayNode(field)}</div>;
  }

  if (field.type === 'subform') {
    return (
      <EFormSubformField field={field} value={raw} readOnly onChange={() => undefined} />
    );
  }

  if (field.type === 'photo') {
    return (
      <EFormPhotoField field={field} value={raw} readOnly onChange={() => undefined} />
    );
  }

  if (fileList) {
    return (
      <EFormFileField
        field={field.type === 'file' ? field : { ...field, type: 'file', multiple: true }}
        value={raw}
        readOnly
        onChange={() => undefined}
      />
    );
  }

  return (
    <div className={cn('text-sm whitespace-pre-wrap break-words', colors.text)} title={full}>
      {field.type === 'textarea' && raw != null ? String(raw) : display}
    </div>
  );
}

function SectionCard({
  section,
  values,
}: {
  section: EFormSectionSchema;
  values: Record<string, unknown>;
}) {
  const { colors } = useTheme();
  const fields = section.fields ?? [];
  if (!fields.length) return null;

  return (
    <section className={cn('rounded-xl border overflow-hidden', colors.border, colors.surface)}>
      <div className={cn('px-4 py-3 border-b', colors.border, 'bg-slate-50/80 dark:bg-slate-900/40')}>
        <h2 className={cn('text-sm font-semibold tracking-tight', colors.text)}>
          {section.heading || 'Section'}
        </h2>
        {section.subHeading ? (
          <p className={cn('text-xs mt-0.5', colors.textMuted)}>{section.subHeading}</p>
        ) : null}
      </div>
      <div className={cn('divide-y', colors.border)}>
        {fields.map((field) => {
          if (isEFormDisplayField(field) && field.type === 'spacer') {
            return (
              <div key={field.id} className="px-4">
                {maybeEFormDisplayNode(field)}
              </div>
            );
          }
          if (isEFormDisplayField(field) && field.type === 'heading') {
            return (
              <div key={field.id} className="px-4 py-3">
                {maybeEFormDisplayNode(field)}
              </div>
            );
          }
          if (isEFormDisplayField(field)) {
            return (
              <div key={field.id} className="px-4 py-3">
                {maybeEFormDisplayNode(field)}
              </div>
            );
          }
          return (
            <div
              key={field.id}
              className="px-4 py-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(5.5rem,8rem)] gap-2 md:gap-6 items-start"
            >
              <div className={cn('text-sm font-medium leading-snug', colors.textSecondary)}>
                {field.label}
                {field.required ? <span className="text-red-500 ml-0.5">*</span> : null}
              </div>
              <div className="md:text-right md:justify-self-end min-w-0">
                <FieldValueBlock field={field} raw={values[field.key]} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const SubmissionDetail: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? parseInt(idParam, 10) : NaN;
  const { data, isLoading, isError, mutate } = useEFormSubmission(Number.isInteger(id) ? id : 0);
  const { colors } = useTheme();
  const { can } = useAuth();
  const accessContext = useEFormAccessContext(data?.definitionId ?? null);
  const { showSuccess, showError } = useNotification();
  const confirm = useConfirm();
  const { setTitleOverride } = useTopbar();
  const navigate = useNavigate();
  const canEdit = can('e_form_submissions', 'edit') && accessContext.data?.canFill !== false;
  const canDelete = can('e_form_submissions', 'delete');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingLayout, setExportingLayout] = useState(false);
  const [layoutTemplateId, setLayoutTemplateId] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);

  const layoutPrintouts = useMemo(
    () => listPrintTemplates(data?.version?.printLayoutJson, 'designed-or-legacy'),
    [data?.version?.printLayoutJson],
  );

  useEffect(() => {
    if (!layoutPrintouts.length) {
      setLayoutTemplateId('');
      return;
    }
    setLayoutTemplateId((current) =>
      layoutPrintouts.some((item) => item.id === current) ? current : layoutPrintouts[0].id,
    );
  }, [layoutPrintouts]);

  useEffect(() => {
    if (!data) return;
    setTitleOverride(data.definition?.name ?? `Submission #${data.id}`);
    return () => setTitleOverride(undefined);
  }, [data, setTitleOverride]);

  const schema: EFormSchema | null = useMemo(() => {
    if (!data?.schemaSnapshot || !isEFormSchema(data.schemaSnapshot)) return null;
    return applyEFormRoleAccess(data.schemaSnapshot, accessContext.data?.role);
  }, [data, accessContext.data?.role]);

  const fields = useMemo(() => (schema ? flattenSchemaFields(schema) : []), [schema]);

  const compiled = useMemo(() => (schema ? compileFormSchema(schema) : null), [schema]);

  const useLayout = fields.some(
    (f) =>
      f.type === 'file' ||
      f.type === 'photo' ||
      f.type === 'signature' ||
      f.type === 'subform' ||
      f.type === 'richtext' ||
      (f.optionsSource === 'reference' && (f.type === 'select' || f.type === 'radio')),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner size="large" message="Loading submission…" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={cn('max-w-lg mx-auto py-16 px-4 text-center', colors.text)}>
        <div
          className={cn(
            'mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border',
            colors.border,
            colors.surface,
          )}
        >
          <DocumentTextIcon className={cn('w-6 h-6', colors.textMuted)} />
        </div>
        <h1 className="text-lg font-semibold">Submission not found</h1>
        <p className={cn('mt-1 text-sm', colors.textMuted)}>
          It may have been deleted, or you do not have access.
        </p>
        <Link to="/e-forms" className="inline-block mt-4">
          <Button variant="secondary">Back to E-Forms</Button>
        </Link>
      </div>
    );
  }

  const values = (data.valuesJson ?? {}) as Record<string, unknown>;
  const formSubmissionsPath = `/e-forms/${data.definitionId}/submissions`;
  const formName = data.definition?.name ?? 'E-Form';
  const formCode = data.definition?.code;
  const isDraft = data.status === 'DRAFT';
  const statusVariant = data.status === 'SUBMITTED' ? 'success' : 'warning';
  const approval = data.approval ?? null;
  const approvalVariant =
    approval?.status === 'APPROVED'
      ? 'success'
      : approval?.status === 'REJECTED' || approval?.status === 'CANCELLED'
        ? 'danger'
        : approval?.status === 'PENDING'
          ? 'warning'
          : 'default';
  const approvalLabel =
    approval?.status === 'PENDING'
      ? 'Pending approval'
      : approval?.status === 'APPROVED'
        ? 'Approved'
        : approval?.status === 'REJECTED'
          ? 'Rejected'
          : approval?.status === 'CANCELLED'
            ? 'Cancelled'
            : approval?.status === 'DRAFT'
              ? 'Approval draft'
              : approval
                ? approval.status
                : null;
  const wasReturned = approval?.notice?.kind === 'returned';
  const wasRejected = approval?.notice?.kind === 'rejected';

  const runStatusAction = async (action: 'submit' | 'revert') => {
    const ok = await confirm(
      action === 'submit'
        ? {
            title: wasReturned ? 'Resubmit for approval' : 'Submit form',
            message: wasReturned
              ? `Resubmit #${data.id} for approval?`
              : `Submit #${data.id}? Submitted records cannot be edited until reverted to draft.`,
            confirmText: wasReturned ? 'Resubmit' : 'Submit',
            cancelText: 'Cancel',
          }
        : {
            title: 'Revert to draft',
            message: `Revert #${data.id} to draft? You will be able to edit it again.`,
            confirmText: 'Revert',
            cancelText: 'Cancel',
            confirmVariant: 'danger',
          },
    );
    if (!ok) return;
    setStatusBusy(true);
    try {
      if (action === 'submit') await submitEFormSubmission(data.id);
      else await revertEFormSubmissionToDraft(data.id);
      showSuccess(
        action === 'submit'
          ? wasReturned
            ? 'Resubmitted for approval'
            : 'Submission submitted'
          : 'Reverted to draft',
      );
      await mutate();
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = ex?.response?.data?.message || ex?.message;
      showError(
        typeof msg === 'string'
          ? msg
          : action === 'submit'
            ? 'Submit failed'
            : 'Revert failed',
      );
    } finally {
      setStatusBusy(false);
    }
  };

  const renderedFields: Record<string, React.ReactNode> = {};
  if (useLayout && compiled) {
    const byKey = new Map(fields.map((f) => [f.key, f]));
    for (const field of compiled.fields) {
      const schemaField = byKey.get(field.name);
      if (schemaField && isEFormDisplayField(schemaField)) {
        renderedFields[field.name] = (
          <div key={field.name}>{maybeEFormDisplayNode(schemaField)}</div>
        );
      } else if (schemaField?.type === 'file') {
        renderedFields[field.name] = (
          <div key={field.name}>
            <div className={cn('text-sm font-medium mb-1', colors.text)}>{schemaField.label}</div>
            <EFormFileField
              field={schemaField}
              value={values[field.name]}
              readOnly
              onChange={() => undefined}
            />
          </div>
        );
      } else if (schemaField?.type === 'photo') {
        renderedFields[field.name] = (
          <div key={field.name}>
            <div className={cn('text-sm font-medium mb-1', colors.text)}>{schemaField.label}</div>
            <EFormPhotoField
              field={schemaField}
              value={values[field.name]}
              readOnly
              onChange={() => undefined}
            />
          </div>
        );
      } else if (schemaField?.type === 'signature') {
        renderedFields[field.name] = (
          <div key={field.name}>
            <div className={cn('text-sm font-medium mb-1', colors.text)}>{schemaField.label}</div>
            <EFormSignatureField
              field={schemaField}
              value={values[field.name]}
              readOnly
              onChange={() => undefined}
            />
          </div>
        );
      } else if (schemaField?.type === 'subform') {
        renderedFields[field.name] = (
          <div key={field.name}>
            <div className={cn('text-sm font-medium mb-1', colors.text)}>{schemaField.label}</div>
            <EFormSubformField
              field={schemaField}
              value={values[field.name]}
              readOnly
              onChange={() => undefined}
            />
          </div>
        );
      } else {
        renderedFields[field.name] = renderField({
          field: { ...field, disabled: true, readOnly: true },
          value: values[field.name],
          onChange: () => undefined,
          getFormData: () => values,
          isEdit: true,
        });
      }
    }
  }

  return (
    <div className={cn('w-full pb-8 -mx-4 px-2 sm:px-3', colors.text)}>
      <div
        className={cn(
          'rounded-xl border overflow-hidden mb-4',
          colors.border,
          colors.surface,
        )}
      >
        <div className="px-4 py-4 sm:px-5 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={statusVariant}>{data.status}</Badge>
              {approval && approvalLabel ? (
                <Link
                  to={`/approvals/${approval.id}`}
                  className="inline-flex items-center gap-1"
                >
                  <Badge variant={approvalVariant}>{approvalLabel}</Badge>
                  <ApprovalNoticeIcon approval={approval} />
                </Link>
              ) : null}
              {formCode ? (
                <span
                  className={cn(
                    'rounded-md border px-2 py-0.5 text-[11px] font-medium tracking-wide',
                    colors.border,
                    colors.textMuted,
                  )}
                >
                  {formCode}
                </span>
              ) : null}
              <span className={cn('text-xs', colors.textMuted)}>#{data.id}</span>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight truncate">
                {formName}
              </h1>
              <p className={cn('mt-1 text-sm', colors.textMuted)}>
                {isDraft
                  ? 'Draft — not yet submitted'
                  : data.submittedAt
                    ? `Submitted ${formatDateTime(data.submittedAt)}`
                    : 'Submitted'}
                {' · '}
                {submitterDisplayName(data)}
              </p>
            </div>
            {schema?.instructions ? (
              <p
                className={cn(
                  'text-sm leading-relaxed rounded-lg border px-3 py-2',
                  colors.border,
                  'bg-slate-50/70 dark:bg-slate-900/30',
                  colors.textSecondary,
                )}
              >
                {schema.instructions}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {approval ? (
              <Link to={`/approvals/${approval.id}`}>
                <Button variant="secondary" className="inline-flex items-center gap-1.5">
                  View approval
                </Button>
              </Link>
            ) : null}
            {data.definition?.sourceFileName ? (
              <Button
                variant="secondary"
                disabled={exporting}
                className="inline-flex items-center gap-1.5"
                title={`Export into ${data.definition.sourceFileName}`}
                onClick={async () => {
                  setExporting(true);
                  try {
                    const { filled } = await exportEFormSubmissionTemplate(data.id);
                    if (filled) {
                      showSuccess('Exported submission into the original file.');
                    } else {
                      showSuccess(
                        'Downloaded the original file. Fillable PDF fields or Word {{placeholders}} were not found, so values were not written in.',
                      );
                    }
                  } catch {
                    // Global API interceptor already toasts.
                  } finally {
                    setExporting(false);
                  }
                }}
              >
                <DownloadIcon className="w-4 h-4" />
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            ) : null}
            {layoutPrintouts.length ? (
              <div className="flex flex-wrap items-center gap-2">
                {layoutPrintouts.length > 1 ? (
                  <Select
                    size="sm"
                    value={layoutTemplateId}
                    onChange={(event) => setLayoutTemplateId(event.target.value)}
                    options={layoutPrintouts.map((item) => ({ value: item.id, label: item.name }))}
                    showClearButton={false}
                  />
                ) : null}
                <Button
                  variant="secondary"
                  disabled={exportingLayout || !layoutTemplateId}
                  className="inline-flex items-center gap-1.5"
                  onClick={async () => {
                    if (!layoutTemplateId) return;
                    setExportingLayout(true);
                    try {
                      await exportEFormSubmissionLayout(data.id, layoutTemplateId);
                      showSuccess('Designed layout PDF downloaded.');
                    } finally {
                      setExportingLayout(false);
                    }
                  }}
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  {exportingLayout
                    ? 'Printing…'
                    : layoutPrintouts.length > 1
                      ? 'Print'
                      : 'Layout PDF'}
                </Button>
              </div>
            ) : null}
            {canEdit && isDraft && !data.locked ? (
              <Button
                variant="default"
                disabled={statusBusy}
                className="inline-flex items-center gap-1.5"
                onClick={() => void runStatusAction('submit')}
              >
                <CheckCircleIcon className="w-4 h-4" />
                {wasReturned ? 'Resubmit for approval' : wasRejected ? 'Submit again' : 'Submit'}
              </Button>
            ) : null}
            {canEdit && !isDraft && !data.locked ? (
              <Button
                variant="secondary"
                disabled={statusBusy}
                className="inline-flex items-center gap-1.5"
                onClick={() => void runStatusAction('revert')}
              >
                <UndoIcon className="w-4 h-4" />
                Revert to draft
              </Button>
            ) : null}
            {data.locked ? (
              <Badge variant="success">Approved · locked</Badge>
            ) : null}
            {canEdit && isDraft && !data.locked ? (
              <Link to={`/e-forms/submissions/${data.id}/edit`}>
                <Button variant="default" className="inline-flex items-center gap-1.5">
                  <PencilIcon className="w-4 h-4" />
                  Edit
                </Button>
              </Link>
            ) : null}
            {canDelete && !data.locked ? (
              <Button
                variant="secondary"
                className="inline-flex items-center gap-1.5 text-red-700 dark:text-red-400"
                onClick={() => setDeleteOpen(true)}
              >
                <TrashIcon className="w-4 h-4" />
                Delete
              </Button>
            ) : null}
          </div>
        </div>

        <div
          className={cn(
            'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 border-t px-4 py-3.5 sm:px-5',
            colors.border,
            'bg-slate-50/50 dark:bg-slate-900/25',
          )}
        >
          <MetaRow icon={<DocumentTextIcon className="w-4 h-4" />} label="Submission" value={`#${data.id}`} />
          <MetaRow
            icon={<UserIcon className="w-4 h-4" />}
            label="Submitted by"
            value={submitterDisplayName(data)}
          />
          <MetaRow
            icon={<CalendarIcon className="w-4 h-4" />}
            label={isDraft ? 'Last updated' : 'Submitted'}
            value={formatDateTime(isDraft ? data.updatedAt : data.submittedAt || data.updatedAt)}
          />
          <MetaRow
            icon={<DocumentTextIcon className="w-4 h-4" />}
            label="Approval"
            value={
              approval ? (
                <span className="inline-flex items-start gap-1.5 min-w-0">
                  <Link
                    to={`/approvals/${approval.id}`}
                    className="underline-offset-2 hover:underline"
                  >
                    {approvalLabel}
                    {approval.status === 'PENDING' && approval.currentStepLabel
                      ? ` · ${approval.currentStepLabel}`
                      : ''}
                  </Link>
                  <ApprovalNoticeIcon approval={approval} className="mt-0.5" />
                </span>
              ) : (
                '—'
              )
            }
          />
          <MetaRow
            icon={<DocumentTextIcon className="w-4 h-4" />}
            label="Form version"
            value={data.version?.version != null ? `v${data.version.version}` : '—'}
          />
        </div>
      </div>

      {approval?.notice ? <ApprovalNoticeBanner approval={approval} className="mb-4" /> : null}

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 px-0.5">
          <h2 className={cn('text-sm font-semibold uppercase tracking-wide', colors.textMuted)}>
            Responses
          </h2>
          <span className={cn('text-xs', colors.textMuted)}>
            {fields.filter((f) => !isEFormDisplayField(f)).length} fields
          </span>
        </div>

        {useLayout && compiled ? (
          <div className={cn('rounded-xl border p-3 sm:p-4', colors.border, colors.surface)}>
            <FormLayoutRenderer
              layout={compiled.formLayout}
              fields={renderedFields}
              formData={values}
            />
          </div>
        ) : schema?.sections?.length ? (
          <div className="space-y-3">
            {schema.sections.map((section) => (
              <SectionCard key={section.id} section={section} values={values} />
            ))}
          </div>
        ) : (
          <div className={cn('rounded-xl border px-4 py-10 text-center text-sm', colors.border, colors.surface, colors.textMuted)}>
            No fields in this form.
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={deleteOpen}
        onClose={() => {
          if (!deleting) setDeleteOpen(false);
        }}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await deleteEFormSubmission(data.id);
            showSuccess('Submission deleted');
            navigate(formSubmissionsPath);
          } catch (err: unknown) {
            const ex = err as { response?: { data?: { message?: string } }; message?: string };
            showError(ex?.response?.data?.message || ex?.message || 'Delete failed');
          } finally {
            setDeleting(false);
          }
        }}
        title="Delete submission"
        message="Delete this submission permanently? This cannot be undone."
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        isLoading={deleting}
      />
    </div>
  );
};

export default SubmissionDetail;
